'use client';

import { useState } from 'react';
import { mailerLite } from '@/lib/mailerlite';

interface FormData {
  name: string;
  email: string;
  companyName: string;
  category: string;
  notes: string;
}

export function SamplePackForm() {
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    companyName: '',
    category: '',
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);

    try {
      const response = await mailerLite.addSubscriber({
        email: formData.email,
        fields: {
          name: formData.name,
          company: formData.companyName,
          product_category: formData.category,
          additional_notes: formData.notes,
          form_type: 'sample_pack',
        },
        groups: ['154344651853137220'], // Using the same group as other forms
      });

      if (!response.success) {
        throw new Error(response.error || 'Failed to submit form');
      }

      setIsSuccess(true);
      setFormData({
        name: '',
        email: '',
        companyName: '',
        category: '',
        notes: '',
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  if (isSuccess) {
    return (
      <div className="bg-green-50 p-6 rounded-xl shadow text-center">
        <h3 className="text-green-800 text-xl font-semibold mb-2">Thank you for your request!</h3>
        <p className="text-green-700">We&apos;ll be in touch shortly to finalize your sample pack.</p>
        <button
          onClick={() => setIsSuccess(false)}
          className="mt-4 text-green-700 underline hover:text-green-800"
        >
          Request another sample pack
        </button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="grid gap-4">
      {error && (
        <div className="bg-red-50 text-red-700 p-3 rounded">
          {error}
        </div>
      )}
      <input
        type="text"
        name="name"
        placeholder="Your Name"
        value={formData.name}
        onChange={handleInputChange}
        required
        className="border p-3 rounded"
      />
      <input
        type="email"
        name="email"
        placeholder="Email Address"
        value={formData.email}
        onChange={handleInputChange}
        required
        className="border p-3 rounded"
      />
      <input
        type="text"
        name="companyName"
        placeholder="Company Name"
        value={formData.companyName}
        onChange={handleInputChange}
        className="border p-3 rounded"
      />
      <select
        name="category"
        value={formData.category}
        onChange={handleInputChange}
        required
        className="border p-3 rounded"
      >
        <option value="">Select Product Category</option>
        <option value="Sportswear">Sportswear</option>
        <option value="Streetwear">Streetwear</option>
      </select>
      <textarea
        name="notes"
        placeholder="Additional Notes"
        value={formData.notes}
        onChange={handleInputChange}
        className="border p-3 rounded"
      ></textarea>
      <button
        type="submit"
        disabled={isLoading}
        className={`button button-primary button-rounded button-large ${
          isLoading ? 'opacity-50 cursor-not-allowed' : ''
        }`}
      >
        {isLoading ? 'Submitting...' : 'Submit Request'}
      </button>
    </form>
  );
}
